import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import Checkmark from '~/icons/checkmark.svg';
import { Chevron } from '~/shared/components/Chevron/Chevron';

const fadeIn = keyframes({
    from: {
        opacity: 0,
        transform: 'translateY(-4px) scale(0.98)',
    },
    to: {
        opacity: 1,
        transform: 'translateY(0) scale(1)',
    },
});

const fadeOut = keyframes({
    from: {
        opacity: 1,
        transform: 'translateY(0) scale(1)',
    },
    to: {
        opacity: 0,
        transform: 'translateY(-4px) scale(0.98)',
    },
});

export const StyledContainer = styled(DropdownMenu.Root)({
    position: 'relative',
});

export const StyledTrigger = styled(DropdownMenu.Trigger)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    color: theme.colors.black,
    fontSize: theme.fontSizes.sm,
    fontFamily: theme.fonts.roboto,
    cursor: 'pointer',
    border: 'none',
    background: 'none',

    ':focus-visible': {
        boxShadow: theme.shadows.accessibility,
    },

    '&[data-state="open"] svg': {
        transform: 'rotateX(180deg)',
    },

    '&[data-state="closed"] svg': {
        transform: 'rotateX(0deg)',
    },
}));

export const StyledChevron = styled(Chevron)(({ theme }) => ({
    marginLeft: theme.space[2],
    padding: theme.space[1],
    width: 22,
    height: 22,
    color: theme.colors.black,

    '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
    },
}));

export const StyledDropdown = styled(DropdownMenu.Content)(({ theme }) => ({
    zIndex: theme.zIndices['1'],
    width: 225,
    maxHeight: 450,
    border: `1px solid ${theme.colors.grey10}`,
    borderRadius: theme.sizes.cornerRadius,
    background: theme.colors.white,
    boxShadow: theme.shadows.floating,
    transformOrigin: 'top right',
    willChange: 'opacity, transform',
    opacity: 0,

    '&[data-state="open"]': {
        animation: `${fadeIn} 200ms cubic-bezier(0.16, 1, 0.3, 1) forwards`,
        pointerEvents: 'auto',
    },

    '&[data-state="closed"]': {
        animation: `${fadeOut} 150ms ease-in forwards`,
        pointerEvents: 'none',
    },

    // We disable animations for users who have enabled "reduced motion" in their accessibility settings.
    '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
        transform: 'none',
    },
}));

export const StyledOption = styled(DropdownMenu.RadioItem)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.space[3],
    cursor: 'pointer',
    whiteSpace: 'nowrap',

    '&[data-state="checked"]': {
        background: theme.colors.grey20,
        fontWeight: theme.fontWeights.medium,
    },

    '&:hover': {
        background: theme.colors.grey10,
    },

    ':focus-visible': {
        boxShadow: theme.shadows.accessibilityInset,
    },
}));

export const StyledCheckmark = styled(Checkmark)(({ theme }) => ({
    flex: '0 0 19px',
    marginLeft: theme.space[4],
    width: 14,
    height: 10,
    color: theme.colors.black,
    fill: theme.colors.black,
}));
